import { UseFormReturnType } from "@mantine/form"
import _ from "lodash"
import qs from "qs"
import { formatQuery, add } from "react-querybuilder"
import { v4 as uuidv4 } from "uuid"

import { AxesResponses } from "@costory/types/endpoints/axes"
import {
  DigestResponses,
  DigestType,
  HiddenItems,
  Position,
} from "@costory/types/endpoints/digest"
import { Filters } from "@costory/types/filters"

import {
  DEFAULT_DATE_FORMAT,
  DEFAULT_DELAY_IN_DAYS,
} from "@costory/shared/const"
import dayjs from "@costory/shared/dayjs"
import {
  getCostValues,
  getdeterministicNodeId,
} from "@costory/shared/utils/digest"
import { computePresetDates } from "@costory/shared/utils/filters"
import { formatNumber } from "@costory/shared/utils/format"

type DigestItemExtended = DigestType["items"][0] & {
  hiddenItems?: HiddenItems
  totalHiddenCostCurrent?: number
  totalHiddenCostPrevious?: number
  isOthers?: boolean
  isRealOthersCategory?: boolean
  costCurrent?: number
  costPrevious?: number
}

declare module "@costory/types/endpoints/digest" {
  namespace DigestType {
    interface Item {
      isOthers?: boolean
    }
  }
}

type TableRow = {
  title: string
  costCurrent: number
  costPrevious: number
  difference: number
  summary: string
  link: string
  depth: number
}

// Constants
const EPSILON = 0.001
const DEFAULT_GROUP_BY = "cos_provider"
const DEFAULT_DISPLAY_OPTIONS = {
  text: "",
  displayContext: false,
  orderByColumn: "absChange" as "cost" | "absChange" | "relativeChange",
  order: "desc" as "asc" | "desc",
  isOthersAtBottom: false,
}

export const getDefaultRootElem = () => {
  return {
    id: getdeterministicNodeId([]),
    parentId: null,
    position: [],
    direction: "Total Cloud Costs",
    displayOptions: DEFAULT_DISPLAY_OPTIONS,
    imgUrl: null,
    note: null,
    filtersUrl: null,
  } as DigestItemExtended
}

export type NodeTree = {
  value: string
  label: string
  nodeProps: DigestItemExtended
  children: NodeTree[]
  isOthers: boolean
  note?: string | null
  data: {
    costPrevious: number
    costCurrent: number
    totalWithHidden?: {
      costPrevious: number
      costCurrent: number
    }
  }
  othersValues?: Position[]
}

export function getEndOfMonthPlusDelay(period: string): string {
  return dayjs(period)
    .endOf("month")
    .add(DEFAULT_DELAY_IN_DAYS + 1, "day")
    .format(DEFAULT_DATE_FORMAT)
}

export function getChild(
  parent: { key: string; value: string }[],
  filtered: DigestResponses.Investigation,
  level: number = 1,
) {
  return filtered.filter((elem) => {
    return (
      parent.every((p) =>
        elem.arr.some((c) => c.key === p.key && c.value === p.value),
      ) && (level == -1 ? true : elem.arr.length === level + parent.length)
    )
  })
}

export function convertToTree(
  nodes: DigestType["items"],
  getDigestData: Map<string, DigestResponses.Investigation[0]>,
  costColumn: string,
) {
  // Mark nodes as "Others" based on various criteria
  const nodesWithMarkedOthers = nodes.map((node: DigestItemExtended) => {
    const hasHiddenItems = !!node.hiddenItems?.length
    const directionIsOthers = node.direction === "Others"
    const isPotentiallyOthers =
      (typeof node.id === "string" && node.id.includes("others-")) ||
      node.isOthers === true ||
      hasHiddenItems ||
      directionIsOthers

    const isRealOthersCategory =
      directionIsOthers && !hasHiddenItems && node.costCurrent !== undefined

    const isFunctionallyOthers =
      hasHiddenItems || (isPotentiallyOthers && !isRealOthersCategory)

    return {
      ...node,
      isOthers: isFunctionallyOthers,
      isRealOthersCategory,
      hiddenItems: node.hiddenItems || [],
      totalHiddenCostCurrent: node.totalHiddenCostCurrent || 0,
      totalHiddenCostPrevious: node.totalHiddenCostPrevious || 0,
    } as DigestItemExtended
  })

  const nodesMap = new Map<string, NodeTree>()
  const parentChildMap = new Map<string, string[]>()

  // Create tree nodes
  nodesWithMarkedOthers.forEach((node: DigestItemExtended) => {
    const data = getDigestData.get(getdeterministicNodeId(node.position))
    const { costCurrent, costPrevious } = getCostValues(data, node, costColumn)

    const treeNode: NodeTree = {
      children: [],
      isOthers: node.isOthers ?? false,
      label: node.id,
      value: node.id,
      nodeProps: {
        ...node,
        hiddenItems: node.hiddenItems || [],
        totalHiddenCostCurrent: node.totalHiddenCostCurrent || 0,
        totalHiddenCostPrevious: node.totalHiddenCostPrevious || 0,
        costCurrent,
        costPrevious,
      },
      note: node.note,

      data: {
        costCurrent,
        costPrevious,
      },
    }
    nodesMap.set(node.id, treeNode)

    if (node.parentId) {
      if (!parentChildMap.has(node.parentId)) {
        parentChildMap.set(node.parentId, [])
      }
      const parent = parentChildMap.get(node.parentId)
      if (parent) {
        parent.push(node.id)
      } else {
        throw new Error(`Parent with id ${node.parentId} not found`)
      }
    }
  })

  // Build parent-child relationships
  nodesMap.forEach((treeNode, nodeId) => {
    const parentId = treeNode.nodeProps.parentId
    if (parentId && nodesMap.has(parentId)) {
      const parentNode = nodesMap.get(parentId)
      if (!parentNode) {
        throw new Error(`Parent with id ${parentId} not found`)
      }
      if (!parentNode.children.some((child) => child.value === nodeId)) {
        parentNode.children.push(treeNode)
      }
    }
  })

  // Process "Others" nodes
  parentChildMap.forEach((_childIds, parentId) => {
    const parentNode = nodesMap.get(parentId)
    if (!parentNode) return

    const parentData = getDigestData.get(
      getdeterministicNodeId(parentNode.nodeProps.position),
    )
    const { costCurrent: parentCostCurrent, costPrevious: parentCostPrevious } =
      getCostValues(parentData, parentNode.nodeProps, costColumn)

    // Calculate sum of children costs (excluding "Others")
    const nonOthersChildren = parentNode.children.filter(
      (child) => !child.isOthers,
    )
    const childrenSumCostCurrent = _.sumBy(
      nonOthersChildren,
      (child) => child.data.costCurrent || 0,
    )
    const childrenSumCostPrevious = _.sumBy(
      nonOthersChildren,
      (child) => child.data.costPrevious || 0,
    )

    // Calculate expected "Others" costs
    const expectedOthersCostCurrent = parentCostCurrent - childrenSumCostCurrent
    const expectedOthersCostPrevious =
      parentCostPrevious - childrenSumCostPrevious

    const othersNode = parentNode.children.find((child) => child.isOthers)
    const othersNodeIndex = parentNode.children.findIndex(
      (child) => child.isOthers,
    )

    const originalOthersDigestItem = nodesWithMarkedOthers.find(
      (n) => n.parentId === parentId && n.isOthers,
    )

    if (othersNode) {
      // Update existing "Others" node
      othersNode.data.costCurrent = expectedOthersCostCurrent
      othersNode.data.costPrevious = expectedOthersCostPrevious
      othersNode.nodeProps.costCurrent = expectedOthersCostCurrent
      othersNode.nodeProps.costPrevious = expectedOthersCostPrevious
      othersNode.label = "Others"

      // Remove "Others" node if it has no cost and no hidden items
      if (
        Math.abs(expectedOthersCostCurrent) < EPSILON &&
        Math.abs(expectedOthersCostPrevious) < EPSILON &&
        (!othersNode.nodeProps.hiddenItems ||
          othersNode.nodeProps.hiddenItems.length === 0)
      ) {
        parentNode.children.splice(othersNodeIndex, 1)
        nodesMap.delete(othersNode.value)
      }
    } else if (
      Math.abs(expectedOthersCostCurrent) > EPSILON ||
      Math.abs(expectedOthersCostPrevious) > EPSILON ||
      (originalOthersDigestItem &&
        originalOthersDigestItem.hiddenItems &&
        originalOthersDigestItem.hiddenItems.length > 0)
    ) {
      // Create new "Others" node
      const baseProps = originalOthersDigestItem || {
        id: `others-${parentId}-${getdeterministicNodeId([
          ...parentNode.nodeProps.position,
        ])}`,
        parentId,
        position: parentNode.nodeProps.position,
        direction: "Others",
        displayOptions: DEFAULT_DISPLAY_OPTIONS,
        imgUrl: null,
        filtersUrl: null,
        note: "",
        hiddenItems: [],
        isRealOthersCategory: false,
      }

      // Calculate total hidden costs using _.sumBy
      const totalHiddenCostCurrent = baseProps.hiddenItems
        ? _.sumBy(baseProps.hiddenItems, (item) => item.costCurrent || 0)
        : 0

      const totalHiddenCostPrevious = baseProps.hiddenItems
        ? _.sumBy(baseProps.hiddenItems, (item) => item.costPrevious || 0)
        : 0

      const newOthersNode: NodeTree = {
        label: "Others",
        value: baseProps.id,
        isOthers: true,
        children: [],
        nodeProps: {
          ...baseProps,
          isOthers: true,
          costCurrent: expectedOthersCostCurrent,
          costPrevious: expectedOthersCostPrevious,
          hiddenItems: baseProps.hiddenItems || [],
          totalHiddenCostCurrent,
          totalHiddenCostPrevious,
        },
        data: {
          costCurrent: expectedOthersCostCurrent,
          costPrevious: expectedOthersCostPrevious,
        },
      }
      parentNode.children.push(newOthersNode)
      nodesMap.set(newOthersNode.value, newOthersNode)
    }
  })

  // Sort children based on display options
  nodesMap.forEach((parentNode) => {
    if (parentNode.children.length > 0) {
      const col = parentNode.nodeProps.displayOptions.orderByColumn
      parentNode.children = _.orderBy(
        parentNode.children,
        (child) => {
          if (child.isOthers) {
            return col === "absChange"
              ? Math.abs(child.data.costCurrent - child.data.costPrevious)
              : col === "cost"
                ? Math.abs(child.data.costCurrent)
                : Math.abs(
                    (child.data.costCurrent - child.data.costPrevious) /
                      (child.data.costPrevious + 0.0000001),
                  )
          }

          const nodeData = getDigestData.get(
            getdeterministicNodeId(child.nodeProps.position),
          )
          const { costCurrent, costPrevious } = getCostValues(
            nodeData,
            child.nodeProps,
            costColumn,
          )
          const diff = costCurrent - costPrevious

          return col === "absChange"
            ? Math.abs(diff)
            : col === "cost"
              ? Math.abs(costCurrent)
              : Math.abs(diff / (costPrevious + 0.0000001))
        },
        parentNode.nodeProps.displayOptions.order,
      )
    }
  })

  // Get root nodes
  const rootNodes: NodeTree[] = []
  nodesMap.forEach((node) => {
    if (!node.nodeProps.parentId) {
      rootNodes.push(node)
    }
  })

  return rootNodes
}

export function getQuery(node: NodeTree, period: string, costColumn = "cost") {
  const { filtersUrl, direction, position } = node.nodeProps

  if (filtersUrl) {
    // Create base query params with required fields
    const queryFormatted = getQueryFormatted(filtersUrl)
    return { queryFormatted }
  } else {
    // Fallback: build query from node position
    const bestGroupBy = direction ?? DEFAULT_GROUP_BY
    let query = { combinator: "and", rules: [] }
    if (!position || position.length === 0) {
      return { queryFormatted: null }
    }

    // Add each position element to the query
    position.forEach((el) => {
      if (el.key && el.value && el.value !== "__NULL_") {
        query = add(
          query,
          {
            id: uuidv4(),
            field: el.key,
            operator: "in",
            value: [el.value],
          },
          [],
        )
      }
    })

    // Only generate query if we have valid rules
    if (query.rules.length === 0) {
      return { queryFormatted: null }
    }

    const explorerDates = computePresetDates(
      "LAST_MONTH",
      getEndOfMonthPlusDelay(period),
    )
    const isLastMonth = dayjs(period).isSame(
      dayjs().subtract(1, "month").startOf("month"),
      "day",
    )
    // Ensure we have all required fields
    const queryParams = {
      metricId: costColumn,
      currency: "USD",
      limit: 10,
      aggBy: "Day",
      groupBy: bestGroupBy,
      from: explorerDates.from,
      to: explorerDates.to,
      previousFrom: explorerDates.previousFrom,
      previousTo: explorerDates.previousTo,
      datePreset: isLastMonth ? "LAST_MONTH" : undefined,
      whereClause: query,
    }

    const queryFormatted = qs.stringify(
      {
        ...queryParams,
        whereClause: encodeURIComponent(
          formatQuery(queryParams.whereClause, "json_without_ids"),
        ),
      },
      { encode: false },
    )
    return { queryFormatted }
  }
}

const getAllDescendantIds = (
  parentId: string,
  items: DigestItemExtended[],
): string[] => {
  const children = items.filter((item) => item.parentId === parentId)
  const descendantIds = children.map((child) => child.id)

  for (const child of children) {
    descendantIds.push(...getAllDescendantIds(child.id, items))
  }

  return descendantIds
}

export const deleteChild = (
  nodeId: string,
  form: UseFormReturnType<DigestType>,
  digestMap: Map<string, DigestResponses.Investigation[0]>,
  costColumn: string,
): void => {
  const items = form.getValues().items as DigestItemExtended[]
  const itemToHide = items.find(
    (item) => item.id === nodeId,
  ) as DigestItemExtended

  if (!itemToHide || itemToHide.isOthers) {
    return
  }

  const allIdsToRemove = [nodeId, ...getAllDescendantIds(nodeId, items)]

  const data = digestMap.get(getdeterministicNodeId(itemToHide.position))
  if (!data) return

  const costCurrent =
    costColumn === "contracted_cost"
      ? data.contracted_cost_test
      : costColumn === "list_cost"
        ? data.list_cost_test
        : data.cost_test || 0
  const costPrevious =
    costColumn === "contracted_cost"
      ? data.contracted_cost_previous
      : costColumn === "list_cost"
        ? data.list_cost_previous
        : data.cost_previous || 0

  const othersNode = items.find(
    (item) =>
      item.direction === "Others" && item.parentId === itemToHide.parentId,
  ) as DigestItemExtended | undefined

  const hiddenItem = {
    position: itemToHide.position,
    direction: itemToHide.direction,
    costCurrent,
    costPrevious,
    id: itemToHide.id,
    imgUrl: itemToHide.imgUrl,
  }

  let updatedItems = items.filter((item) => !allIdsToRemove.includes(item.id))
  const parentId = itemToHide.parentId
  if (parentId) {
    const remainingSiblings = updatedItems.filter(
      (item) => item.parentId === parentId && item.direction !== "Others",
    )
    if (remainingSiblings.length === 0) {
      updatedItems = updatedItems.filter(
        (item) => !(item.parentId === parentId && item.direction === "Others"),
      )
    }
  }

  if (othersNode) {
    const updatedOthersNode = {
      ...othersNode,
      hiddenItems: [...(othersNode.hiddenItems || []), hiddenItem],
      totalHiddenCostCurrent:
        (othersNode.totalHiddenCostCurrent || 0) + costCurrent,
      totalHiddenCostPrevious:
        (othersNode.totalHiddenCostPrevious || 0) + costPrevious,
      isRealOthersCategory: false,
    }
    const othersIndex = updatedItems.findIndex(
      (item) => item.id === othersNode.id,
    )
    if (othersIndex !== -1) {
      updatedItems[othersIndex] = updatedOthersNode
    }
    form.setValues({ ...form.getValues(), items: updatedItems })
  } else {
    const newOthersNode: DigestItemExtended = {
      id: `${itemToHide.parentId}-${getdeterministicNodeId(
        itemToHide.position.filter((item) => item.key !== itemToHide.direction),
      )}`,
      parentId: itemToHide.parentId,
      position: itemToHide.position.filter(
        (item) => item.key !== itemToHide.direction,
      ),
      direction: "Others",
      displayOptions: DEFAULT_DISPLAY_OPTIONS,
      note: "",
      imgUrl: null,
      filtersUrl: null,
      isOthers: true,
      isRealOthersCategory: false,
      hiddenItems: [hiddenItem],
      totalHiddenCostCurrent: costCurrent,
      totalHiddenCostPrevious: costPrevious,
    }

    updatedItems.push(newOthersNode)
    form.setValues({ ...form.getValues(), items: updatedItems })
  }
}

export function getQueryFormatted(filtersUrl: Filters) {
  const queryParams = {
    ...filtersUrl,
    from: dayjs(filtersUrl.from).format(DEFAULT_DATE_FORMAT),
    to: dayjs(filtersUrl.to).format(DEFAULT_DATE_FORMAT),
    previousFrom: dayjs(filtersUrl.previousFrom).format(DEFAULT_DATE_FORMAT),
    previousTo: dayjs(filtersUrl.previousTo).format(DEFAULT_DATE_FORMAT),
    datePreset: filtersUrl.datePreset ?? undefined,
    bookmarkedEvents: Array.isArray(filtersUrl.bookmarkedEvents)
      ? filtersUrl.bookmarkedEvents.join(",")
      : (filtersUrl.bookmarkedEvents ?? undefined),
    eventsFilter: JSON.stringify(filtersUrl.eventsFilter),
  }

  const queryFormatted = qs.stringify(
    {
      ...queryParams,
      whereClause: encodeURIComponent(
        formatQuery(queryParams.whereClause, "json_without_ids"),
      ),
    },
    { encode: false },
  )
  return queryFormatted
}

export function unhideItem(
  othersNodeId: string,
  hiddenItemId: string,
  form: UseFormReturnType<DigestType>,
) {
  const nodes = form.getValues().items
  const othersIdx = nodes.findIndex((n) => n.id === othersNodeId)
  const othersNode = nodes[othersIdx] as DigestItemExtended

  const itemToUnhide = othersNode.hiddenItems?.find(
    (el) => el.id === hiddenItemId,
  )
  if (!itemToUnhide || !othersNode.hiddenItems) {
    return
  }

  const newNode: DigestItemExtended = {
    id: getdeterministicNodeId(itemToUnhide.position),
    parentId: othersNode.parentId,
    position: itemToUnhide.position,
    direction: itemToUnhide.direction,
    displayOptions: DEFAULT_DISPLAY_OPTIONS,
    note: "",
    imgUrl: itemToUnhide.imgUrl,
    filtersUrl: null,
    costCurrent: itemToUnhide.costCurrent,
    costPrevious: itemToUnhide.costPrevious,
  }

  const updatedOthersNode = {
    ...othersNode,
    hiddenItems: othersNode.hiddenItems.filter((el) => el.id !== hiddenItemId),
    totalHiddenCostCurrent:
      (othersNode.totalHiddenCostCurrent || 0) -
      (itemToUnhide.costCurrent || 0),
    totalHiddenCostPrevious:
      (othersNode.totalHiddenCostPrevious || 0) -
      (itemToUnhide.costPrevious || 0),
  }

  form.replaceListItem("items", othersIdx, updatedOthersNode)
  form.insertListItem("items", newNode)
}

export const calculateDiff = ({
  costColumn,
  cost_test,
  cost_previous,
  contracted_cost_test,
  contracted_cost_previous,
  list_cost_test,
  list_cost_previous,
}: Pick<
  DigestResponses.Investigation[0],
  | "cost_test"
  | "contracted_cost_test"
  | "cost_previous"
  | "contracted_cost_previous"
  | "list_cost_test"
  | "list_cost_previous"
> & {
  costColumn: string
}): number => {
  return costColumn === "contracted_cost"
    ? contracted_cost_test - contracted_cost_previous
    : costColumn === "list_cost"
      ? list_cost_test - list_cost_previous
      : cost_test - cost_previous
}

export function getOptions(
  data: DigestResponses.Investigation,
  parent: { key: string; value: string }[],
  costColumn: string,
) {
  const optionsAvailable = _(getChild(parent, data, 1))
    .groupBy(
      (d) =>
        d.arr.filter((k) => parent.map((l) => l.key).indexOf(k.key) == -1)[0]
          .key,
    )
    .map((values, key) => {
      return {
        direction: key,
        totalDiff: Math.round(
          _.sum(
            values
              .filter(
                (el) => _.sum(el.arr.map((e) => e.value === "__NULL_")) == 0,
              )
              .map((el) =>
                calculateDiff({
                  costColumn,
                  cost_test: el.cost_test,
                  cost_previous: el.cost_previous,
                  contracted_cost_test: el.contracted_cost_test,
                  contracted_cost_previous: el.contracted_cost_previous,
                  list_cost_test: el.list_cost_test,
                  list_cost_previous: el.list_cost_previous,
                }),
              ),
          ),
        ),
        nbrValue: values.length,
        sampleValues: _(values)
          .orderBy(
            (a) =>
              calculateDiff({
                costColumn,
                cost_test: a.cost_test,
                cost_previous: a.cost_previous,
                contracted_cost_test: a.contracted_cost_test,
                contracted_cost_previous: a.contracted_cost_previous,
                list_cost_test: a.list_cost_test,
                list_cost_previous: a.list_cost_previous,
              }),
            "desc",
          )
          .slice(0, 5)
          .map((el) => el.arr.filter((e) => e.key == key)[0].value)
          .value(),
      }
    })
    .value()
  const filtered = _(optionsAvailable)
    .orderBy((d) => d.totalDiff, "desc")
    .value()
  return filtered
}

export function addChild(
  form: UseFormReturnType<DigestType>,
  direction: string,
  data: DigestResponses.Investigation,
  parentId: string | null,
  expandTree: () => void,
  costColumn: string,
  minDiffToHide: number,
  minRelativeDiffToHide: number,
) {
  const nodes = form.getValues().items
  const parentNode = nodes.find((el) => el.id === parentId)
  const parentPosition = parentId == null ? [] : (parentNode?.position ?? [])

  const childs = getChild(parentPosition, data, 1).filter((elem) =>
    elem.arr.some((e) => e.key === direction),
  )

  const childsWithId = childs.map((el) => {
    const id = getdeterministicNodeId(el.arr)
    form.insertListItem("items", {
      id,
      displayOptions: DEFAULT_DISPLAY_OPTIONS,
      direction,
      position: el.arr,
      note: null,
      parentId,
      imgUrl: null,
      filtersUrl: null,
    } as DigestType["items"][0])
    return { id, el }
  })

  const childIds = mergeInOthers(
    childsWithId,
    form,
    data,
    expandTree,
    costColumn,
    minDiffToHide,
    minRelativeDiffToHide,
  )
  childIds.forEach((child) => {
    const childNode = form
      .getValues()
      .items.find((item) => item.id === child.id)
    if (!childNode) throw new Error(`Child with id ${child.id} not found`)
    recursivelyOpen(
      form,
      data,
      childNode,
      costColumn,
      minDiffToHide,
      minRelativeDiffToHide,
    )
  })
  expandTree()

  return childsWithId
}

export const initializeDigest = (
  form: UseFormReturnType<DigestType>,
  digest: DigestResponses.Investigation,
  costColumn: string,
  minDiffToHide: number,
  minRelativeDiffToHide: number,
) => {
  const items = form.getValues().items
  const dad = items.find((item) => item.position.length === 0)
  if (dad) {
    recursivelyOpen(
      form,
      digest,
      dad,
      costColumn,
      minDiffToHide,
      minRelativeDiffToHide,
    )
  }
}

const recursivelyOpen = (
  form: UseFormReturnType<DigestType>,
  digest: DigestResponses.Investigation,
  node: DigestItemExtended,
  costColumn: string,
  minDiffToHide: number,
  minRelativeDiffToHide: number,
) => {
  const childs = getOptions(digest, node.position, costColumn)
  if (childs.length == 1) {
    deleteAllChilds(node.id, form)
    addChild(
      form,
      childs[0].direction,
      digest,
      node.id,
      () => {},
      costColumn,
      minDiffToHide,
      minRelativeDiffToHide,
    )
  }
}

function mergeInOthers(
  childsWithId: { id: string; el: DigestResponses.Investigation[0] }[],
  form: UseFormReturnType<DigestType>,
  digest: DigestResponses.Investigation,
  expandTree: () => void,
  costColumn: string,
  minDiffToHide: number,
  minRelativeDiffToHide: number,
) {
  const childsWithIdWithScore = _(childsWithId)
    .map((el) => ({
      ...el,
      diff: Math.abs(
        calculateDiff({
          costColumn,
          cost_test: el.el.cost_test,
          cost_previous: el.el.cost_previous,
          contracted_cost_test: el.el.contracted_cost_test,
          contracted_cost_previous: el.el.contracted_cost_previous,
          list_cost_test: el.el.list_cost_test,
          list_cost_previous: el.el.list_cost_previous,
        }),
      ),
      score:
        calculateDiff({
          costColumn,
          cost_test: el.el.cost_test,
          cost_previous: el.el.cost_previous,
          contracted_cost_test: el.el.contracted_cost_test,
          contracted_cost_previous: el.el.contracted_cost_previous,
          list_cost_test: el.el.list_cost_test,
          list_cost_previous: el.el.list_cost_previous,
        }) ** 2,
    }))
    .orderBy((el) => el.score, "desc")
    .value()
  const curve = childsWithIdWithScore.map((el) => el.score)
  const idxOfBestPoint = findFarthestPoint(curve) + 1

  const totalDiff = _.sumBy(childsWithIdWithScore, (el) => el.diff)
  // Delete childs with absolute diff < 3% of total absolute diff
  const childsToDeleteBecauseOfDiff = childsWithIdWithScore.filter(
    (child) =>
      child.diff <= 0.03 * totalDiff ||
      Math.abs(child.diff) <= minDiffToHide ||
      child.diff /
        (costColumn === "contracted_cost"
          ? child.el.contracted_cost_previous
          : child.el.cost_previous) <=
        minRelativeDiffToHide,
  )
  const childsToDelete = childsWithIdWithScore
    // delete childs that are after the elbow point
    .slice(idxOfBestPoint)
    // Delete childs with absolute diff < 3% of total absolute diff
    .filter(
      (el) =>
        el.diff /
          (costColumn === "contracted_cost"
            ? el.el.contracted_cost_previous
            : el.el.cost_previous) <=
        1,
    )
    // Exclude from deletion childs with relative diff > 50%

    .concat(childsToDeleteBecauseOfDiff)

  if (childsToDelete.length > 1) {
    const digestMap = new Map(
      digest.map((d) => [getdeterministicNodeId(d.arr), d]),
    )
    childsToDelete.forEach((child) => {
      deleteChild(child.id, form, digestMap, costColumn)
    })
    expandTree()
    return childsWithIdWithScore.filter(
      (child) => !childsToDelete.map((c) => c.id).includes(child.id),
    )
  }
  return childsWithId
}

export function deleteAllChilds(
  value: string,
  form: UseFormReturnType<DigestType>,
) {
  const nodes = form.getValues().items

  const allIdsToRemove = getAllDescendantIds(value, nodes)
  const toKeep = nodes.filter((el) => !allIdsToRemove.includes(el.id))
  form.setValues({ items: toKeep })
}

export function treeToBulletPoints(
  node: NodeTree,
  mapper: Map<string, string>,
  dateThreshold: string,
  depth = 0,
  baseUrl: string,
) {
  // Skip 'Others' nodes
  if (node.isOthers) {
    return ""
  }

  let bulletPoints = ""
  // Indentation based on depth
  const indentation = "\t".repeat(depth)
  // Add the current node as a bullet point
  const label = formatSlice(node, node.nodeProps.direction, mapper, true)
  const id = getdeterministicNodeId(node.nodeProps.position)
  const link = id
    ? `[Open](https://app.costory.io/${baseUrl}/${id}?mode=preview)`
    : ""

  const summary = node.nodeProps.summary?.trim()
  bulletPoints += `${indentation}- ${label} ${link}`
  if (summary) {
    bulletPoints += ` (${summary})`
  }
  bulletPoints += "\n"

  node.children.forEach((child) => {
    bulletPoints += treeToBulletPoints(
      child,
      mapper,
      dateThreshold,
      depth + 1,
      baseUrl,
    )
  })

  return bulletPoints
}

export function formatSlice(
  node: NodeTree,
  direction?: string,
  mapper?: Map<string, string>,
  showText?: boolean,
  hideDiff?: boolean,
) {
  const label = `: ${formatNumber({ value: node.data.costPrevious })} -> ${formatNumber({ value: node.data.costCurrent })}`
  const directionLabel = mapper
    ? mapper.get(direction!) || direction
    : direction
  const relativeDiff = formatNumber({
    value:
      (node.data.costCurrent - node.data.costPrevious) / node.data.costPrevious,
    numberType: "percent",
    extraDigits: 1,
  })
  const diffFormatted = formatNumber({
    value: node.data.costCurrent - node.data.costPrevious,
  })

  if (node.isOthers) {
    return `${directionLabel} others: ${diffFormatted} ${label || ""} ${showText ? node.nodeProps.displayOptions.text : ""}`
  }

  if (node.nodeProps.position.length == 0) {
    return `${directionLabel} ${label || ""} ${showText ? node.nodeProps.displayOptions.text : ""}`
  }

  if (direction) {
    const axis = node.nodeProps.position.find(
      (el: Position[0]) => el.key === direction,
    )?.value
    return `${directionLabel}: ${axis}: ${diffFormatted} ${!hideDiff ? `(\` ${relativeDiff}\`)` : ""} ${label || ""} ${showText ? node.nodeProps.displayOptions.text : ""}`
  }

  return ""
}

export const getReactQueryBuilderQuery = (el: Position[0]) =>
  el.value != "__NULL_"
    ? {
        field: el.key,
        operator: "in",
        value: [el.value],
      }
    : { field: el.key, operator: "null", value: null }

export function getquery(node: NodeTree, costColumn = "cost") {
  const bestGroupBy =
    node.children.map((el) => el.nodeProps.direction)[0] ?? DEFAULT_GROUP_BY
  let query = { combinator: "and", rules: [] }
  node.nodeProps.position.forEach((el: Position[0]) => {
    query = add(query, getReactQueryBuilderQuery(el), [])
  })
  const queryFormatted =
    node.nodeProps.position.length !== 0
      ? qs.stringify(
          {
            metricId: costColumn,
            currency: "USD",
            limit: 10,
            aggBy: "Day",
            groupBy: bestGroupBy,
            ...computePresetDates("LAST_MONTH"),
            whereClause: encodeURIComponent(
              formatQuery(query, "json_without_ids"),
            ),
          },
          {
            encode: false,
          },
        )
      : null
  return { queryFormatted }
}
function treeToTableRows(
  nodes: NodeTree[],
  mapper: Map<string, string>,
  baseUrl: string,
  depth = 0,
): Array<TableRow> {
  let rows: Array<TableRow> = []
  nodes.forEach((node) => {
    if (node.isOthers) {
      return
    }

    const id = getdeterministicNodeId(node.nodeProps.position)
    const link = id
      ? `https://app.costory.io/${baseUrl}/${id}?mode=preview`
      : ""

    let title = ""
    const direction = node.nodeProps.direction

    if (node.nodeProps.position.length === 0) {
      title = direction
    } else {
      const directionLabel = mapper?.get(direction) || direction
      const axis = node.nodeProps.position.find(
        (el: Position[0]) => el.key === direction,
      )?.value
      title = axis ? `${directionLabel}: ${axis}` : directionLabel
    }

    const summary = node.nodeProps.summary?.trim() || ""
    const difference = node.data.costCurrent - node.data.costPrevious
    console.log(difference)
    rows.push({
      title,
      costCurrent: node.data.costCurrent,
      costPrevious: node.data.costPrevious,
      difference,
      summary,
      link,
      depth,
    })

    if (node.children.length > 0) {
      rows = rows.concat(
        treeToTableRows(node.children, mapper, baseUrl, depth + 1),
      )
    }
  })

  return rows
}

export function formatTableAsMarkdown(
  data: NodeTree[],
  mapper: Map<string, string>,
  baseUrl: string,
): string {
  const tableData = data.flatMap((node) =>
    treeToTableRows([node], mapper, baseUrl, 0),
  )

  if (tableData.length === 0) {
    return "No data available"
  }

  let markdown =
    "| Title | Cost Current Month | Cost Previous Month | Difference | Summary | Link |\n"
  markdown +=
    "|-------|-------------------|--------------------|-----------|---------|------|\n"

  tableData.forEach((row) => {
    const indent = "\t".repeat(row.depth)
    const title = `${indent}${row.title.replace(/\|/g, "\\|")}`
    const costCurrent = formatNumber({ value: row.costCurrent })
    const costPrevious = formatNumber({ value: row.costPrevious })
    const difference = formatNumber({ value: row.difference })
    const summary = row.summary?.trim()
    const link = row.link ? `[Open](${row.link})` : ""

    markdown += `| ${title} | ${costCurrent} | ${costPrevious} | ${difference} | ${summary} | ${link} |\n`
  })

  return markdown
}

export function formatMarkdown(
  form: UseFormReturnType<DigestType>,
  axes: AxesResponses.Axis[],
  digestData: Map<string, DigestResponses.Investigation[0]>,
  dateThreshold: string,
  costColumn: string,
  baseUrl: string,
  exportAsTable: boolean,
) {
  const mapper = new Map<string, string>()
  axes.forEach((elem) => mapper.set(elem.name, elem.label))
  const data = convertToTree(form.getValues().items, digestData, costColumn)
  if (exportAsTable) {
    return formatTableAsMarkdown(data, mapper, baseUrl)
  }

  return data.map((el) =>
    treeToBulletPoints(el, mapper, dateThreshold, 0, baseUrl),
  )
}

export const replaceDigestItem = (
  form: UseFormReturnType<DigestType>,
  value: Partial<DigestItemExtended>,
  id: string,
) => {
  const items = form.getValues().items
  const index = items.findIndex((el) => el.id == id)

  if (index === -1) {
    console.error(
      `Item with id ${id} not found when trying to replace digest item`,
    )
    return
  }

  const currentItem = items[index] as DigestItemExtended

  const updatedItem = {
    ...currentItem,
    ...value,
    hiddenItems:
      value.hiddenItems !== undefined
        ? value.hiddenItems
        : currentItem.hiddenItems,
  }

  form.replaceListItem("items", index, updatedItem)
}

function findFarthestPoint(curve: number[]): number {
  const nPoints: number = curve.length

  // If there are less than 2 points, a line cannot be formed, so no meaningful distance can be calculated.
  if (nPoints < 2) {
    return 0 // Or throw an error, depending on desired behavior for edge cases.
  }

  // Create an array of coordinates [x, y] for each point.
  // In Python, np.vstack((range(nPoints), curve)).T creates [[0, curve[0]], [1, curve[1]], ...]
  const allCoord: number[][] = _.range(nPoints).map((x, i) => [x, curve[i]])

  const firstPoint: number[] = allCoord[0]
  const lastPoint: number[] = allCoord[nPoints - 1]

  // Calculate the vector representing the line from the first to the last point.
  const lineVec: number[] = [
    lastPoint[0] - firstPoint[0],
    lastPoint[1] - firstPoint[1],
  ]

  // Calculate the magnitude (Euclidean norm) of the line vector.
  const lineVecMagnitude: number = Math.sqrt(lineVec[0] ** 2 + lineVec[1] ** 2)

  // Normalize the line vector (make it a unit vector).
  let lineVecNorm: number[]
  if (lineVecMagnitude === 0) {
    // Handle the case where the first and last points are identical.
    // In this scenario, all points are effectively on the "line" (a single point).
    lineVecNorm = [0, 0]
  } else {
    lineVecNorm = [lineVec[0] / lineVecMagnitude, lineVec[1] / lineVecMagnitude]
  }

  // Calculate vectors from the first point to all other points.
  const vecFromFirst: number[][] = allCoord.map((coord) => [
    coord[0] - firstPoint[0],
    coord[1] - firstPoint[1],
  ])

  // Calculate the scalar projection of each vecFromFirst onto lineVecNorm.
  // This is equivalent to np.sum(vecFromFirst * np.matlib.repmat(lineVecNorm, nPoints, 1), axis=1)
  // which calculates the dot product for each vector.
  const scalarProduct: number[] = vecFromFirst.map(
    (vec) => vec[0] * lineVecNorm[0] + vec[1] * lineVecNorm[1],
  )

  // Calculate the component of vecFromFirst that is parallel to the line.
  // This is equivalent to np.outer(scalarProduct, lineVecNorm).
  const vecFromFirstParallel: number[][] = scalarProduct.map((scalar) => [
    scalar * lineVecNorm[0],
    scalar * lineVecNorm[1],
  ])

  // Calculate the vector from each point to the line (perpendicular component).
  const vecToLine: number[][] = vecFromFirst.map((vec, i) => [
    vec[0] - vecFromFirstParallel[i][0],
    vec[1] - vecFromFirstParallel[i][1],
  ])

  // Calculate the distance from each point to the line (magnitude of vecToLine).
  const distToLine: number[] = vecToLine.map((vec) =>
    Math.sqrt(vec[0] ** 2 + vec[1] ** 2),
  )

  // Find the index of the point with the maximum distance to the line.
  const idxOfBestPoint: number = _.indexOf(distToLine, _.max(distToLine))

  return idxOfBestPoint
}

// Build the node path for a node, for use in tooltips in model header
export function buildNodePath(
  node: NodeTree,
  nodesMap: Map<string, NodeTree>,
  queryMapperData?: Map<string, string>,
): string[] {
  const path: string[] = []
  let current: NodeTree | null = node
  for (;;) {
    if (!current || !current.nodeProps) break
    const valueLabel =
      current!.nodeProps.position?.find(
        (p) => p.key === current!.nodeProps.direction,
      )?.value || ""
    const label =
      `${queryMapperData?.get(current!.nodeProps.direction) || current!.nodeProps.direction}${valueLabel ? ` ${valueLabel}` : ""}`.trim()
    path.unshift(label)
    if (current!.nodeProps.parentId) {
      // Find parent by iterating through nodesMap since we're using deterministic IDs as keys
      let parent: NodeTree | null = null
      nodesMap.forEach((node) => {
        if (node.nodeProps.id === current!.nodeProps.parentId) {
          parent = node
        }
      })
      current = parent
    } else {
      current = null
    }
  }
  return path
}

export function getPercentageChange(current: number, previous: number): number {
  if (previous === 0) {
    return current === 0 ? 0 : 100
  }
  return ((current - previous) / previous) * 100
}
